import type {
  WidgetButtonShape,
  WidgetButtonSize,
  WidgetPosition,
  WidgetTemplateStyle,
} from '@/hooks/api/useWidgets'

// ============================================================================
// Form state
// ============================================================================

/** Colours are held as typed hex text so a half-typed value never resets. */
export interface WidgetStyleFormState {
  shape: WidgetButtonShape
  size: WidgetButtonSize
  iconOnly: boolean
  position: WidgetPosition
  offsetX: string
  offsetY: string
  buttonColor: string
  iconColor: string
  panelColor: string
  actionColor: string
}

/** The brand palette the Colors "Reset" returns to. */
export const WIDGET_BRAND_COLORS = {
  buttonColor: '#36D26A',
  iconColor: '#FFFFFF',
  panelColor: '#FFFFFF',
  actionColor: '#222222',
} as const

/** The launcher placement the Placement "Reset" returns to. */
export const WIDGET_PLACEMENT_DEFAULTS = {
  position: 'bottom-left',
  offsetX: '24',
  offsetY: '24',
} as const satisfies Pick<WidgetStyleFormState, 'position' | 'offsetX' | 'offsetY'>

export const WIDGET_BUTTON_SHAPES: readonly WidgetButtonShape[] = ['circle', 'square']
export const WIDGET_BUTTON_SIZES: readonly WidgetButtonSize[] = ['S', 'M', 'L']

/** Only the bottom corners exist — the launcher is anchored to the page floor. */
export const WIDGET_CORNERS: readonly WidgetPosition[] = ['bottom-left', 'bottom-right']

/** Launcher diameter per size, as the preview renders it. */
export const WIDGET_BUTTON_PIXELS: Record<WidgetButtonSize, number> = { S: 40, M: 48, L: 56 }

/** Offsets are pixels on the host page; past this the launcher leaves the fold. */
export const WIDGET_OFFSET_MAX = 400

export function createDefaultStyleFormState(): WidgetStyleFormState {
  return {
    shape: 'circle',
    size: 'S',
    iconOnly: false,
    ...WIDGET_PLACEMENT_DEFAULTS,
    ...WIDGET_BRAND_COLORS,
  }
}

export function fromTemplateStyle(style: WidgetTemplateStyle): WidgetStyleFormState {
  const defaults = createDefaultStyleFormState()

  return {
    shape: style.shape ?? defaults.shape,
    size: style.size ?? defaults.size,
    iconOnly: style.icon_only ?? defaults.iconOnly,
    position: style.position,
    offsetX: String(style.offset_x),
    offsetY: String(style.offset_y),
    buttonColor: style.colors.whatsapp_button ?? defaults.buttonColor,
    iconColor: style.colors.whatsapp_icon ?? defaults.iconColor,
    panelColor: style.colors.panel_background,
    actionColor: style.colors.action_buttons,
  }
}

function parseOffsetNumber(value: string): number {
  const parsed = Number.parseInt(value, 10)

  if (!Number.isFinite(parsed)) {
    return Number(WIDGET_PLACEMENT_DEFAULTS.offsetX)
  }

  return Math.min(Math.max(parsed, 0), WIDGET_OFFSET_MAX)
}

export function toTemplateStyle(state: WidgetStyleFormState): WidgetTemplateStyle {
  return {
    launcher_mode: 'floating',
    position: state.position,
    offset_x: parseOffsetNumber(state.offsetX),
    offset_y: parseOffsetNumber(state.offsetY),
    shape: state.shape,
    size: state.size,
    icon_only: state.iconOnly,
    colors: {
      action_buttons: state.actionColor,
      panel_background: state.panelColor,
      whatsapp_button: state.buttonColor,
      whatsapp_icon: state.iconColor,
    },
  }
}

// ============================================================================
// Colour helpers
// ============================================================================

const HEX_COLOR = /^#[0-9a-f]{6}$/i

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR.test(value.trim())
}

/**
 * Near-white swatches need an outline of their own, or they disappear into the
 * field they sit in. Uses perceived luminance so a pale yellow counts as light.
 */
export function isLightColor(value: string): boolean {
  const hex = value.trim().replace('#', '')

  if (hex.length !== 6) {
    return false
  }

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  return 0.299 * red + 0.587 * green + 0.114 * blue > 225
}
